import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Path from 'routes/Path';
import { userRoles } from 'constants/users';
import { layouts, editorTypes, buttonIds } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import {
  maxWidth,
  FreecodeNav,
  MenuIcon,
  NavbarContents,
  DropdownContent,
  NavbarContentRow,
  HeaderLink,
  LinkSpacer,
  LogoImg,
  LogoImgSmall,
  SpectateCountContainer,
} from './styles';
import TourButton from './TourButton';
import ReportButton from './ReportButton';
import CreatorButtons from './CreatorButtons';
import CreatorInspectButtons from './CreatorInspectButtons';
import PublishButton from './PublishButton';
import UserDetails from './UserDetails';
import SponsorDisplay from './SponsorDisplay';
import FreecodeProgressBar from './FreecodeProgressBar';
import { Images } from 'assets/images';
import { useAppContext, useFreecodeContextLayout, useFreecodeContextData } from 'hooks/useContext';
import { useWindowSize } from 'hooks/useWindowSize';
import { RoundIconButton } from 'components/Button/IconButton';
import LayoutButton from './LayoutButton';

const headerLinksCompany = [
  { link: Path.testsPage, label: 'Tests' },
  { link: Path.sendTestPage, label: 'Send Test' },
];

const getLogoUrl = (userRole, isTicketAuth) => {
  if (isTicketAuth) return Path.candidateTicketsPage;
  if (userRole && userRole !== 'candidate') return Path.testsPage;
  return Path.landingPage;
};

const getUserUrl = (userRole, isTicketAuth, isCommunityUser) => {
  if (isTicketAuth) return Path.candidateTicketsPage;
  if (userRole && userRole !== 'candidate') return Path.testsPage;
  if (isCommunityUser) return Path.profilePage;
  return Path.landingPage;
};

const getHeaderLinks = (userRole, isTicketAuth, headerLinks) => {
  if (!isTicketAuth && userRole && userRole !== userRoles.CANDIDATE) {
    return [...headerLinks, ...headerLinksCompany];
  }
  return headerLinks;
};

export default function HeaderFreecode({ contest, headerLinks = [] }) {
  const { userRole, isCommunityUser, isTicketAuth } = useSelector((state) => state.auth);
  let { spectatorsCount } = useSelector((state) => state.freecode);

  const { isDarkMode, toggleDarkMode } = useAppContext();
  const { layout, editorType } = useFreecodeContextLayout();
  const { test, sponsor, ticket } = useFreecodeContextData();

  const { width } = useWindowSize();

  const [useSmallLogo, setUseSmallLogo] = useState(false);
  const [isDropdown, setIsDropdown] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  const logoUrl = useMemo(() => getLogoUrl(userRole, isTicketAuth), [userRole, isTicketAuth]);

  const userUrl = useMemo(
    () => getUserUrl(userRole, isTicketAuth, isCommunityUser),
    [userRole, isTicketAuth, isCommunityUser]
  );

  const actualHeaderLinks = useMemo(
    () => getHeaderLinks(userRole, isTicketAuth, headerLinks),
    [userRole, isTicketAuth, headerLinks]
  );

  useEffect(() => {
    if (!isDropdown && width < maxWidth) setIsDropdown(true);
    else if (isDropdown && width > maxWidth) setIsDropdown(false);
    if (sponsor || ticket) {
      if (!useSmallLogo && width < 400) setUseSmallLogo(true);
      else if (useSmallLogo && width > 400) setUseSmallLogo(false);
    }
  }, [width, isDropdown, setIsDropdown, sponsor, ticket, useSmallLogo, setUseSmallLogo]);

  const renderHeaderLinks = () => (
    <>
      {actualHeaderLinks.map((linkData, index) => (
        <NavbarContentRow key={index}>
          <HeaderLink href={linkData.link}>{linkData.label}</HeaderLink>
        </NavbarContentRow>
      ))}
      <LinkSpacer />
    </>
  );

  const renderCreatorButton = () =>
    editorType === editorTypes.creator &&
    test && (
      <NavbarContentRow>
        <PublishButton />
      </NavbarContentRow>
    );

  const renderRoundButtonRow = () => (
    <>
      <NavbarContentRow>
        {editorType === editorTypes.creator && test && <CreatorButtons />}
        {editorType === editorTypes.creatorInspect && test && <CreatorInspectButtons />}
        {editorType !== editorTypes.creator &&
          editorType !== editorTypes.creatorInspect &&
          editorType !== editorTypes.liveCoding &&
          test && <ReportButton test={test} contest={contest} />}
        <RoundIconButton
          id={buttonIds.toggleDarkMode}
          icon={isDarkMode ? 'fa-solid fa-sun' : 'fa-regular fa-moon'}
          title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
          onClick={() => toggleDarkMode()}
        />
        {layout !== layouts.small && <TourButton layout={layout} />}
      </NavbarContentRow>
    </>
  );

  const renderDropdown = () => (
    <DropdownContent>
      {ticket && editorType === editorTypes.freecode && (
        <SpectateCountContainer title="Viewer count">
          <i className="fa-solid fa-eye" />
          {spectatorsCount[ticket?._id]}
        </SpectateCountContainer>
      )}
      {renderHeaderLinks()}
      <LayoutButton />
      {renderCreatorButton()}
      {renderRoundButtonRow()}
      <UserDetails userRedirectUrl={userUrl} />
    </DropdownContent>
  );

  return (
    <FreecodeNav>
      <a href={logoUrl}>
        {useSmallLogo ? <LogoImgSmall src={Images.logoOnlyWhite} alt="FS" /> : <LogoImg src={Images.logo} alt="FS" />}
      </a>
      {isDropdown && sponsor && <SponsorDisplay sponsor={sponsor} contest={contest} />}
      {isDropdown && ticket && editorType !== editorTypes.freecodeInspect && <FreecodeProgressBar ticket={ticket} />}

      <MenuIcon onClick={() => setIsOpened(!isOpened)} />

      <NavbarContents>
        {renderHeaderLinks()}
        {sponsor && <SponsorDisplay sponsor={sponsor} contest={contest} />}
        {ticket && editorType !== editorTypes.freecodeInspect && <FreecodeProgressBar ticket={ticket} />}
        {ticket && editorType === editorTypes.freecode && (
          <SpectateCountContainer title="Viewer count">
            <i className="fa-solid fa-eye" />
            {spectatorsCount[ticket?._id]}
          </SpectateCountContainer>
        )}
        <LayoutButton />
        {renderCreatorButton()}
        {renderRoundButtonRow()}
        <UserDetails userRedirectUrl={userUrl} />
      </NavbarContents>

      {isOpened && renderDropdown()}
    </FreecodeNav>
  );
}
